
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  supabase,
  type CreatorApplication,
  type VideoSubmission,
  type CreatorProfile,
} from '../../lib/supabase';
import AdminLayout from '../../components/feature/AdminLayout';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<CreatorApplication[]>([]);
  const [videos, setVideos] = useState<VideoSubmission[]>([]);
  const [creators, setCreators] = useState<CreatorProfile[]>([]);
  const [loading, setLoading] = useState(true);

  const ADMIN_EMAILS = ['yirun0206@gmail.com'];

  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }
    if (!ADMIN_EMAILS.includes(user.email || '')) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [applicationsRes, videosRes, creatorsRes] = await Promise.all([
        supabase.from('creator_applications').select('*'),
        supabase.from('video_submissions').select('*'),
        supabase.from('creator_profiles').select('*'),
      ]);
      if (applicationsRes.data) setApplications(applicationsRes.data);
      if (videosRes.data) setVideos(videosRes.data);
      if (creatorsRes.data) setCreators(creatorsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="總覽">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  const stats = {
    applications: {
      total: applications.length,
      pending: applications.filter((a) => a.status === 'pending').length,
    },
    videos: {
      total: videos.length,
      pending: videos.filter((v) => v.status === 'pending').length,
      totalViews: videos.reduce((sum, v) => sum + v.view_count, 0),
    },
    creators: {
      total: creators.length,
      totalRewards: creators.reduce((sum, c) => sum + c.total_rewards, 0),
    },
  };

  return (
    <AdminLayout title="總覽">
      {/* ===== 電腦版 ===== */}
      <div className="hidden md:block max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-white mb-2">管理後台總覽</h1>
          <p className="text-slate-400">歡迎回來，這裡是您的管理控制中心</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 rounded-xl p-6 border border-teal-500/30">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-500/30 rounded-lg">
                <i className="ri-team-line text-2xl text-teal-300"></i>
              </div>
              <Link
                to="/admin/creators"
                className="text-teal-300 hover:text-teal-200 text-sm cursor-pointer whitespace-nowrap"
              >
                查看詳情 →
              </Link>
            </div>
            <p className="text-teal-200 text-sm mb-1">創作者總數</p>
            <p className="text-4xl font-bold text-white">{stats.creators.total}</p>
          </div>

          <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl p-6 border border-amber-500/30">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-500/30 rounded-lg">
                <i className="ri-video-line text-2xl text-amber-300"></i>
              </div>
              <Link
                to="/admin/videos"
                className="text-amber-300 hover:text-amber-200 text-sm cursor-pointer whitespace-nowrap"
              >
                查看詳情 →
              </Link>
            </div>
            <p className="text-amber-200 text-sm mb-1">影片總數</p>
            <p className="text-4xl font-bold text-white">{stats.videos.total}</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-xl p-6 border border-orange-500/30">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-500/30 rounded-lg">
                <i className="ri-eye-line text-2xl text-orange-300"></i>
              </div>
            </div>
            <p className="text-orange-200 text-sm mb-1">總觀看數</p>
            <p className="text-4xl font-bold text-white">
              {stats.videos.totalViews.toLocaleString()}
            </p>
          </div>

          <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl p-6 border border-emerald-500/30">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/30 rounded-lg">
                <i className="ri-money-dollar-circle-line text-2xl text-emerald-300"></i>
              </div>
            </div>
            <p className="text-emerald-200 text-sm mb-1">總發放獎金</p>
            <p className="text-4xl font-bold text-white">
              {/* Fixed template string usage */}
              {`NT${stats.creators.totalRewards.toLocaleString()}`}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Link
            to="/admin/applications"
            className="bg-slate-800/80 rounded-xl p-8 border border-slate-700 hover:border-teal-500/50 transition-all duration-300 group cursor-pointer"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-yellow-500/20 rounded-xl group-hover:bg-yellow-500/30 transition-colors">
                <i className="ri-file-list-3-line text-3xl text-yellow-400"></i>
              </div>
              {stats.applications.pending > 0 && (
                <span className="px-4 py-2 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-medium border border-yellow-500/30">
                  {stats.applications.pending} 待審核
                </span>
              )}
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">創作者申請管理</h3>
            <p className="text-slate-400 mb-4">
              審核新的創作者申請，管理申請狀態
            </p>
            <div className="flex items-center text-teal-400 group-hover:text-teal-300">
              <span className="font-medium">前往管理</span>
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
            </div>
          </Link>

          <Link
            to="/admin/videos"
            className="bg-slate-800/80 rounded-xl p-8 border border-slate-700 hover:border-teal-500/50 transition-all duration-300 group cursor-pointer"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-500/20 rounded-xl group-hover:bg-teal-500/30 transition-colors">
                <i className="ri-video-line text-3xl text-teal-400"></i>
              </div>
              {stats.videos.pending > 0 && (
                <span className="px-4 py-2 bg-teal-500/20 text-teal-300 rounded-full text-sm font-medium border border-teal-500/30">
                  {stats.videos.pending} 待審核
                </span>
              )}
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">影片審核管理</h3>
            <p className="text-slate-400 mb-4">
              審核創作者提交的影片，管理獎金發放
            </p>
            <div className="flex items-center text-teal-400 group-hover:text-teal-300">
              <span className="font-medium">前往管理</span>
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
            </div>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">最新申請</h3>
              <Link to="/admin/applications" className="text-teal-400 text-sm cursor-pointer">
                查看全部
              </Link>
            </div>
            <div className="p-6">
              {applications.length === 0 ? (
                <p className="text-slate-500 text-center py-4">暫無申請</p>
              ) : (
                applications.slice(0, 5).map((app) => (
                  <div
                    key={app.id}
                    className="flex items-center justify-between py-3 border-b border-slate-700 last:border-0"
                  >
                    <div>
                      <p className="text-white font-medium">{app.name}</p>
                      <p className="text-slate-400 text-sm">{app.game_type}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        app.status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : app.status === 'approved'
                          ? 'bg-teal-500/20 text-teal-300'
                          : 'bg-red-500/20 text-red-300'
                      }`}
                    >
                      {app.status === 'pending'
                        ? '待審核'
                        : app.status === 'approved'
                        ? '已通過'
                        : '已拒絕'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">最新影片</h3>
              <Link to="/admin/videos" className="text-teal-400 text-sm cursor-pointer">
                查看全部
              </Link>
            </div>
            <div className="p-6">
              {videos.length === 0 ? (
                <p className="text-slate-500 text-center py-4">暫無影片</p>
              ) : (
                videos.slice(0, 5).map((video) => (
                  <div
                    key={video.id}
                    className="flex items-center justify-between py-3 border-b border-slate-700 last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{video.video_title}</p>
                      <p className="text-slate-400 text-sm">{video.creator_name}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ml-4 whitespace-nowrap ${
                        video.status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : video.status === 'approved'
                          ? 'bg-teal-500/20 text-teal-300'
                          : 'bg-red-500/20 text-red-300'
                      }`}
                    >
                      {video.status === 'pending'
                        ? '待審核'
                        : video.status === 'approved'
                        ? '已通過'
                        : '已拒絕'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ===== 手機版 ===== */}
      <div className="md:hidden px-4 pt-4">
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-slate-900 rounded-xl p-3.5 border border-slate-800/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 flex items-center justify-center bg-teal-500/20 rounded-lg">
                <i className="ri-team-line text-sm text-teal-400"></i>
              </div>
              <span className="text-slate-500 text-xs">創作者</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.creators.total}</p>
          </div>
          <div className="bg-slate-900 rounded-xl p-3.5 border border-slate-800/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 flex items-center justify-center bg-amber-500/20 rounded-lg">
                <i className="ri-video-line text-sm text-amber-400"></i>
              </div>
              <span className="text-slate-500 text-xs">影片</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.videos.total}</p>
          </div>
          <div className="bg-slate-900 rounded-xl p-3.5 border border-slate-800/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 flex items-center justify-center bg-rose-500/20 rounded-lg">
                <i className="ri-eye-line text-sm text-rose-400"></i>
              </div>
              <span className="text-slate-500 text-xs">總觀看</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {stats.videos.totalViews.toLocaleString()}
            </p>
          </div>
          <div className="bg-slate-900 rounded-xl p-3.5 border border-slate-800/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 flex items-center justify-center bg-emerald-500/20 rounded-lg">
                <i className="ri-money-dollar-circle-line text-sm text-emerald-400"></i>
              </div>
              <span className="text-slate-500 text-xs">總獎金</span>
            </div>
            <p className="text-xl font-bold text-white">
              {/* Fixed template string usage for mobile view */}
              {`NT${stats.creators.totalRewards.toLocaleString()}`}
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-5">
          <Link
            to="/admin/applications"
            className="flex items-center justify-between bg-slate-900 rounded-xl p-4 border border-slate-800/60 active:bg-slate-800 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-500/15 rounded-xl">
                <i className="ri-file-list-3-line text-lg text-amber-400"></i>
              </div>
              <div>
                <p className="text-white text-sm font-medium">申請管理</p>
                <p className="text-slate-500 text-xs">審核創作者申請</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {stats.applications.pending > 0 && (
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded-full text-[10px] font-semibold">
                  {stats.applications.pending} 待審
                </span>
              )}
              <i className="ri-arrow-right-s-line text-slate-600"></i>
            </div>
          </Link>
          <Link
            to="/admin/videos"
            className="flex items-center justify-between bg-slate-900 rounded-xl p-4 border border-slate-800/60 active:bg-slate-800 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-500/15 rounded-xl">
                <i className="ri-video-line text-lg text-teal-400"></i>
              </div>
              <div>
                <p className="text-white text-sm font-medium">影片審核</p>
                <p className="text-slate-500 text-xs">管理影片與獎金</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {stats.videos.pending > 0 && (
                <span className="px-2 py-0.5 bg-teal-500/20 text-teal-300 rounded-full text-[10px] font-semibold">
                  {stats.videos.pending} 待審
                </span>
              )}
              <i className="ri-arrow-right-s-line text-slate-600"></i>
            </div>
          </Link>
        </div>

        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white text-sm font-semibold">最新申請</h2>
            <Link to="/admin/applications" className="text-teal-400 text-xs cursor-pointer">
              查看全部
            </Link>
          </div>
          <div className="space-y-2">
            {applications.length === 0 ? (
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-800/60 text-center">
                <p className="text-slate-500 text-xs">暫無申請</p>
              </div>
            ) : (
              applications.slice(0, 4).map((app) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between bg-slate-900 rounded-xl px-4 py-3 border border-slate-800/60"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 flex items-center justify-center bg-slate-800 rounded-full flex-shrink-0">
                      <i className="ri-user-line text-sm text-slate-400"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{app.name}</p>
                      <p className="text-slate-500 text-[11px] truncate">{app.game_type}</p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ml-2 ${
                      app.status === 'pending'
                        ? 'bg-amber-500/20 text-amber-300'
                        : app.status === 'approved'
                        ? 'bg-teal-500/20 text-teal-300'
                        : 'bg-red-500/20 text-red-300'
                    }`}
                  >
                    {app.status === 'pending'
                      ? '待審核'
                      : app.status === 'approved'
                      ? '已通過'
                      : '已拒絕'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white text-sm font-semibold">最新影片</h2>
            <Link to="/admin/videos" className="text-teal-400 text-xs cursor-pointer">
              查看全部
            </Link>
          </div>
          <div className="space-y-2">
            {videos.length === 0 ? (
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-800/60 text-center">
                <p className="text-slate-500 text-xs">暫無影片</p>
              </div>
            ) : (
              videos.slice(0, 4).map((video) => (
                <div
                  key={video.id}
                  className="flex items-center justify-between bg-slate-900 rounded-xl px-4 py-3 border border-slate-800/60"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 flex items-center justify-center bg-slate-800 rounded-full flex-shrink-0">
                      <i className="ri-play-circle-line text-sm text-slate-400"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{video.video_title}</p>
                      <p className="text-slate-500 text-[11px] truncate">{video.creator_name}</p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ml-2 ${
                      video.status === 'pending'
                        ? 'bg-amber-500/20 text-amber-300'
                        : video.status === 'approved'
                        ? 'bg-teal-500/20 text-teal-300'
                        : 'bg-red-500/20 text-red-300'
                    }`}
                  >
                    {video.status === 'pending'
                      ? '待審核'
                      : video.status === 'approved'
                      ? '已通過'
                      : '已拒絕'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
